/**
 * GraphQL Error Handling Utilities for AppSync
 *
 * Provides utilities for handling partial data responses where some fields
 * succeed while others fail (common with owner-based authorization).
 *
 * @see portal/src/lib/README.md for full documentation
 */

export enum GraphQLErrorType {
  UNAUTHORIZED = "Unauthorized",
  VALIDATION_ERROR = "ValidationError",
}

/**
 * Minimal interface for GraphQL errors that works with both
 * the graphql package and Amplify/AppSync's error format.
 *
 * AppSync returns errorType at the top level, while standard GraphQL
 * uses extensions.errorType. We support both.
 */
export interface GraphQLErrorLike {
  message: string;
  /** AppSync returns errorType at the top level */
  errorType?: string;
  /** Standard GraphQL uses extensions.errorType */
  extensions?: { errorType?: string } & Record<string, unknown>;
  path?: readonly (string | number)[];
  locations?: readonly { line: number; column: number }[];
}

/**
 * Enhanced Error type for GraphQL errors with additional metadata.
 * Used by the graphql() wrapper and recognized by errorReporter.
 */
export interface GraphQLError extends Error {
  name: "GraphQLError";
  /** Fatal errors that caused the request to fail */
  graphQLErrors: GraphQLErrorLike[];
  /** Auth/partial errors that were suppressed (if any) */
  partialErrors?: GraphQLErrorLike[];
  /** Original response for debugging (when available) */
  originalResponse?: { data: unknown; errors: GraphQLErrorLike[] };
}

/**
 * Type guard to check if an error is a GraphQLError.
 */
export function isGraphQLError(error: unknown): error is GraphQLError {
  return (
    error instanceof Error &&
    error.name === "GraphQLError" &&
    "graphQLErrors" in error &&
    Array.isArray((error as GraphQLError).graphQLErrors)
  );
}

/**
 * Get the error type from a GraphQL error, checking both AppSync and standard formats.
 */
export function getErrorType(error: GraphQLErrorLike): string | undefined {
  return error.errorType ?? error.extensions?.errorType;
}

/**
 * Check if a GraphQL error has a specific error type.
 * Handles both AppSync (top-level) and standard GraphQL (extensions) formats.
 */
export function hasErrorType(
  error: GraphQLErrorLike,
  errorType: GraphQLErrorType | string,
): boolean {
  return getErrorType(error) === errorType;
}

/**
 * Check if any error in an array has a specific error type.
 */
export function hasAnyErrorOfType(
  errors: readonly GraphQLErrorLike[],
  errorType: GraphQLErrorType | string,
): boolean {
  return errors.some((error) => hasErrorType(error, errorType));
}

/**
 * Creates a GraphQLError with proper metadata for error reporting.
 *
 * @param fatal - Fatal errors that should cause the request to fail
 * @param partial - Partial/auth errors that were suppressed
 * @param context - Optional context message to prepend
 * @returns A GraphQLError with all metadata attached
 */
export function createGraphQLError(
  fatal: GraphQLErrorLike[],
  partial: GraphQLErrorLike[] = [],
  context?: string,
): GraphQLError {
  const errorMessage = formatErrors(fatal);
  const message = context ? `${context}: ${errorMessage}` : errorMessage;

  const error = new Error(message) as GraphQLError;
  error.name = "GraphQLError";
  error.graphQLErrors = fatal;
  if (partial.length > 0) {
    error.partialErrors = partial;
  }

  return error;
}

/**
 * Check if an error is a partial/expected error that shouldn't fail the request.
 * Currently this includes authorization errors from owner-based access control.
 *
 * Partial errors occur when:
 * - A query includes nested fields the user doesn't have access to
 * - Owner-based auth restricts access to specific records
 * - The main data is returned successfully, but some nested fields are null
 *
 * @param error - A GraphQL error from AppSync
 * @returns true if this error is expected and the response data is still usable
 */
export function isPartialError(error: GraphQLErrorLike): boolean {
  // Auth errors are expected when using owner-based auth
  // The data we can access is returned; unauthorized fields are null
  return (
    hasErrorType(error, GraphQLErrorType.UNAUTHORIZED) ||
    error.message?.includes("Not Authorized")
  );
}

/**
 * Partition GraphQL errors into partial (ignorable) and fatal errors.
 *
 * @param errors - Array of GraphQL errors from a response
 * @returns Object with partial errors (can be ignored) and fatal errors (should throw)
 */
export function partitionErrors(
  errors: readonly GraphQLErrorLike[] | undefined,
): {
  partial: GraphQLErrorLike[];
  fatal: GraphQLErrorLike[];
} {
  if (!errors || errors.length === 0) {
    return { partial: [], fatal: [] };
  }

  const partial: GraphQLErrorLike[] = [];
  const fatal: GraphQLErrorLike[] = [];

  for (const error of errors) {
    if (isPartialError(error)) {
      partial.push(error);
    } else {
      fatal.push(error);
    }
  }

  return { partial, fatal };
}

/**
 * Format errors for display/logging.
 *
 * @param errors - Array of GraphQL errors
 * @returns Semicolon-separated error messages
 */
export function formatErrors(errors: readonly GraphQLErrorLike[]): string {
  return errors.map((e) => e.message).join("; ");
}

/**
 * Parse subscription errors from Amplify/AppSync's nested format.
 *
 * Subscription errors arrive as connection-level errors with embedded GraphQL errors:
 * ```json
 * {"errors":[{"message":"Connection failed: {\"errors\":[{\"errorType\":\"Unauthorized\",...}]}"}]}
 * ```
 *
 * This function extracts the inner GraphQL errors so they can be classified
 * using `partitionErrors` (auth vs fatal).
 *
 * @param error - Raw error from a subscription's error callback
 * @returns Array of parsed GraphQLErrorLike objects
 */
export function parseSubscriptionError(error: unknown): GraphQLErrorLike[] {
  if (!error || typeof error !== "object") {
    return [{ message: String(error) }];
  }

  // Handle { errors: [...] } format
  if (
    "errors" in error &&
    Array.isArray((error as { errors: unknown }).errors)
  ) {
    const outerErrors = (error as { errors: unknown[] }).errors;
    const parsed: GraphQLErrorLike[] = [];

    for (const outerErr of outerErrors) {
      if (
        !outerErr ||
        typeof outerErr !== "object" ||
        !("message" in outerErr)
      ) {
        continue;
      }

      const message = (outerErr as { message: string }).message;

      // Try to extract nested JSON from messages like "Connection failed: {\"errors\":[...]}"
      const jsonMatch = message.match(/\{[\s\S]*"errors"[\s\S]*\}/);
      if (jsonMatch) {
        try {
          const inner = JSON.parse(jsonMatch[0]) as { errors?: unknown[] };
          if (inner.errors && Array.isArray(inner.errors)) {
            for (const innerErr of inner.errors) {
              if (
                innerErr &&
                typeof innerErr === "object" &&
                "message" in innerErr
              ) {
                parsed.push(innerErr as GraphQLErrorLike);
              }
            }
            continue;
          }
        } catch {
          // JSON parse failed, fall through to treat outer error as-is
        }
      }

      // Fallback: use the outer error directly
      parsed.push(outerErr as GraphQLErrorLike);
    }

    if (parsed.length > 0) {
      return parsed;
    }
  }

  // Handle plain Error objects
  if (error instanceof Error) {
    return [{ message: error.message }];
  }

  return [{ message: String(error) }];
}
